import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { statusService } from '../services/api';
import { Table, Pagination, StatusToggle, PageHeader, useToast, Card } from './common';
import { usePageTitle } from '../context/PageTitleContext';
import { useSidebar } from '../context/SidebarContext';
import { formatDateDDMMYYYY } from '../utils/dateUtils';
import usePageState from '../hooks/usePageState';
import { useAuth } from '../context/AuthContext';

const StatusManager = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { getPagePermissions } = useAuth();
  const permissions = getPagePermissions('/admin/statuses');
  const { setPageTitle } = usePageTitle();
  const { isCollapsed } = useSidebar();
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState({});
  const [pagination, setPagination] = useState({
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 30
  });

  const [filters, setFilters] = usePageState('statusManager', {
    currentPage: 1,
    itemsPerPage: 30,
    searchTerm: '',
  }, ['/admin/statuses']);
  const { currentPage, itemsPerPage, searchTerm } = filters;
  const setCurrentPage  = (v) => setFilters({ currentPage: v });
  const setItemsPerPage = (v) => setFilters({ itemsPerPage: v });
  const setSearchTerm   = (v) => setFilters({ searchTerm: v });

  useEffect(() => {
    setPageTitle('Statuses');
  }, [setPageTitle]);

  useEffect(() => {
    fetchStatuses();
  }, [currentPage, searchTerm, itemsPerPage]);

  const fetchStatuses = async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, limit: itemsPerPage };
      if (searchTerm) params.search = searchTerm;
      
      const response = await statusService.getAll(params);
      setStatuses(response.data.data);
      setPagination(response.data.pagination);
    } catch (err) {
      setError('Failed to fetch statuses');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const handleView = (status) => {
    navigate(`/admin/statuses/view/${status.id}`);
  };

  const handleEdit = (status) => {
    navigate(`/admin/statuses/edit/${status.id}`);
  };

  const handleAddStatus = () => {
    navigate('/admin/statuses/create');
  };

  const handleDelete = async (id) => {
    const confirmed = await toast.confirm('Are you sure you want to delete this status?');
    if (confirmed) {
      try {
        await statusService.delete(id);
        toast.error('Status deleted successfully');
        fetchStatuses();
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to delete status');
        console.error(err);
      }
    }
  };

  const handleStatusToggle = async (status, newStatus) => {
    setUpdatingStatus(prev => ({ ...prev, [status.id]: true }));
    
    try {
      await statusService.updateStatus(status.id, newStatus);
      toast.success(`Status ${newStatus ? 'activated' : 'deactivated'} successfully`);
      
      // Refresh the statuses list
      await fetchStatuses();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update status');
      console.error(err);
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [status.id]: false }));
    }
  };

  return (
    <div className="admin-panel h-full">
      <Card compact={isCollapsed} allowOverflow={true} noBottomPadding={true} className="h-full flex flex-col">
        <PageHeader 
          addButton={permissions.can_add ? {
            label: 'Add Status',
            onClick: handleAddStatus,
            variant: 'primary'
          } : null}
          filters={{
            search: {
              value: searchTerm,
              onChange: handleSearchChange,
              placeholder: 'Search statuses...'
            }
          }}
        />
        {error && (
          <div className="p-4 mb-4 bg-danger-50 text-danger-700 rounded-md border-l-4 border-danger-600">
            {error}
          </div>
        )}

        <Table
              columns={[
                { 
                  header: 'S.No', 
                  accessor: 'serial',
                  width: '80px',
                  render: (_, row, index) => (currentPage - 1) * pagination.itemsPerPage + index + 1
                },
                { 
                  header: 'Name', 
                  accessor: 'name',
                  width: '200px',
                  render: (value, row) => (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <span 
                        style={{ 
                          display: 'inline-block',
                          width: '20px',
                          height: '20px',
                          backgroundColor: row.color,
                          borderRadius: '4px',
                          border: '2px solid #e5e7eb'
                        }}
                        title={row.color}
                      />
                      <span className="text-sm text-gray-900" title={value}>
                        {value && value.length > 35 ? `${value.substring(0, 35)}...` : value}
                      </span>
                    </div>
                  )
                },
                { 
                  header: 'Priority', 
                  accessor: 'priority',
                  width: '100px',
                  align: 'center',
                  render: (value) => (
                    <span className="text-sm text-gray-900">
                      {value || 0}
                    </span>
                  )
                },
                {
                  header: 'Created',
                  accessor: 'created_at',
                  width: '120px',
                  render: (value) => formatDateDDMMYYYY(value),
                },
                {
                  header: 'Status',
                  accessor: 'status',
                  width: '100px',
                  align: 'center',
                  render: (value, row) => (
                    <StatusToggle
                      status={value}
                      onChange={(newStatus) => handleStatusToggle(row, newStatus)}
                      loading={updatingStatus[row.id]}
                      disabled={!permissions.can_edit}
                    />
                  )
                },
                {
                  header: 'Actions',
                  accessor: 'actions',
                  width: '120px',
                  align: 'center',
                  actions: [
                    ...(permissions.can_edit ? [{ type: 'edit', onClick: handleEdit }] : []),
                    ...(permissions.can_delete ? [{ type: 'delete', onClick: (row) => handleDelete(row.id) }] : [])
                  ]
                },
              ]}
              data={permissions.can_list ? statuses : []}
              onRowClick={permissions.can_view ? handleView : undefined}
              emptyMessage={!permissions.can_list ? 'Permission Denied - You don\'t have permission to view this data' : 'No statuses found'}
              loading={loading}
            />
            
            <Pagination
              currentPage={currentPage}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={handleItemsPerPageChange}
            />
            
      </Card>
    </div>
  );
};

export default StatusManager;
